import { useState } from 'react';
import { FileText, Download, Eye, ChevronRight, X, Search, Upload, AlertCircle, CheckCircle2, Camera, Sparkles } from 'lucide-react';
import { useClientSettings } from './settings';
import ReceiptSnap from './ReceiptSnap';

type FileItem = {
  id: string;
  name: string;
  category: 'Tax Return' | 'BAS' | 'P&L' | 'Letters' | 'Other';
  date: string;
  size: string;
  year: string;
};

const files: FileItem[] = [
  { id: '1', name: 'Tax Return FY2024-25.pdf', category: 'Tax Return', date: '12 Apr 2026', size: '2.4 MB', year: '2026' },
  { id: '2', name: 'Profit & Loss - Mar 2026.pdf', category: 'P&L', date: '8 Apr 2026', size: '480 KB', year: '2026' },
  { id: '3', name: 'BAS Q3 2026.pdf', category: 'BAS', date: '5 Apr 2026', size: '320 KB', year: '2026' },
  { id: '4', name: 'Letter of Income.pdf', category: 'Letters', date: '28 Mar 2026', size: '180 KB', year: '2026' },
  { id: '5', name: 'Profit & Loss - Feb 2026.pdf', category: 'P&L', date: '4 Mar 2026', size: '472 KB', year: '2026' },
  { id: '6', name: 'BAS Q2 2026.pdf', category: 'BAS', date: '28 Jan 2026', size: '316 KB', year: '2026' },
  { id: '7', name: 'Tax Return FY2023-24.pdf', category: 'Tax Return', date: '15 Aug 2025', size: '2.1 MB', year: '2025' },
  { id: '8', name: 'Profit & Loss - FY2023-24.pdf', category: 'P&L', date: '14 Aug 2025', size: '520 KB', year: '2025' },
  { id: '9', name: 'BAS Q4 2025.pdf', category: 'BAS', date: '28 Jul 2025', size: '298 KB', year: '2025' },
];

const categoryColors: Record<FileItem['category'], string> = {
  'Tax Return': 'bg-violet-500/15 text-violet-700 dark:text-violet-300',
  'BAS': 'bg-blue-500/15 text-blue-700 dark:text-blue-300',
  'P&L': 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  'Letters': 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  'Other': 'bg-muted text-muted-foreground',
};

type Requested = {
  id: string;
  title: string;
  description: string;
  dueDate: string;
  status: 'pending' | 'uploaded';
};

const initialRequests: Requested[] = [
  { id: 'r1', title: 'Q4 bank statement', description: 'CommBank business account · Jan-Mar 2026', dueDate: 'Due 30 Apr', status: 'pending' },
  { id: 'r2', title: 'Vehicle logbook', description: '12 weeks ending March 2026', dueDate: 'Due 5 May', status: 'pending' },
  { id: 'r3', title: 'New equipment receipts', description: 'Asset purchases over $1,000 in March', dueDate: 'Uploaded 8 Apr', status: 'uploaded' },
];

export default function FilesPage() {
  const { t } = useClientSettings();
  const [search, setSearch] = useState('');
  const [previewFile, setPreviewFile] = useState<FileItem | null>(null);
  const [requests, setRequests] = useState<Requested[]>(initialRequests);
  const [receiptOpen, setReceiptOpen] = useState(false);

  const markUploaded = (id: string) => {
    setRequests(prev => prev.map(r => (r.id === id ? { ...r, status: 'uploaded', dueDate: 'Uploaded just now' } : r)));
  };

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  const filtered = files.filter(f =>
    f.name.toLowerCase().includes(search.toLowerCase()) ||
    f.category.toLowerCase().includes(search.toLowerCase())
  );

  const grouped = filtered.reduce<Record<string, FileItem[]>>((acc, f) => {
    if (!acc[f.year]) acc[f.year] = [];
    acc[f.year].push(f);
    return acc;
  }, {});

  return (
    <div className="space-y-5 pt-2">
      <div>
        <h1 className="text-2xl font-bold text-foreground">{t('my_files')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('all_documents')}</p>
      </div>

      {/* Snap a receipt CTA */}
      <button
        onClick={() => setReceiptOpen(true)}
        className="flex w-full items-center gap-4 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/15 via-primary/5 to-card p-4 text-left transition-colors hover:from-primary/20"
      >
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-md">
          <Camera className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-semibold text-foreground">Snap a receipt</p>
            <span className="flex items-center gap-0.5 rounded-full bg-primary/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-primary">
              <Sparkles className="h-2.5 w-2.5" />
              AI
            </span>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">Vendor, amount, GST and category extracted automatically</p>
        </div>
        <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
      </button>

      {/* Requested files */}
      <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-500 text-white">
              <Upload className="h-4 w-4" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">{t('requested_files')}</p>
              {pendingCount > 0 && (
                <p className="text-xs text-amber-700 dark:text-amber-400">{pendingCount} pending</p>
              )}
            </div>
          </div>
        </div>
        <div className="mt-3 space-y-2">
          {requests.map(r => (
            <div
              key={r.id}
              className={`flex items-center gap-3 rounded-xl bg-card p-3 ${r.status === 'uploaded' ? 'opacity-70' : ''}`}
            >
              <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${
                r.status === 'uploaded'
                  ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                  : 'bg-amber-500/15 text-amber-700 dark:text-amber-300'
              }`}>
                {r.status === 'uploaded' ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-foreground">{r.title}</p>
                <p className="truncate text-xs text-muted-foreground">{r.description}</p>
                <p className={`mt-0.5 text-[11px] ${
                  r.status === 'uploaded'
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-amber-700 dark:text-amber-400'
                }`}>
                  {r.dueDate}
                </p>
              </div>
              {r.status === 'pending' && (
                <button
                  onClick={() => markUploaded(r.id)}
                  className="flex-shrink-0 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
                >
                  {t('upload')}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          placeholder={t('search_files')}
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full rounded-2xl border border-border bg-card py-3 pl-10 pr-4 text-sm placeholder:text-muted-foreground focus:border-ring focus:outline-none"
        />
      </div>

      {/* File groups by year */}
      {Object.keys(grouped).length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card py-12 text-center">
          <FileText className="mx-auto h-10 w-10 text-muted-foreground/50" />
          <p className="mt-3 text-sm font-medium text-foreground">No files found</p>
          <p className="mt-1 text-xs text-muted-foreground">Try a different search term</p>
        </div>
      ) : (
        Object.entries(grouped)
          .sort((a, b) => parseInt(b[0]) - parseInt(a[0]))
          .map(([year, items]) => (
            <div key={year}>
              <h2 className="mb-2 px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">FY {year}</h2>
              <div className="space-y-2">
                {items.map(file => (
                  <button
                    key={file.id}
                    onClick={() => setPreviewFile(file)}
                    className="group flex w-full items-center gap-3 rounded-2xl border border-border bg-card p-3.5 text-left transition-colors hover:border-muted-foreground/30 hover:bg-muted/40"
                  >
                    <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-muted">
                      <FileText className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-foreground">{file.name}</p>
                      <div className="mt-1 flex items-center gap-2">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${categoryColors[file.category]}`}>
                          {file.category}
                        </span>
                        <span className="text-xs text-muted-foreground">{file.date}</span>
                        <span className="text-xs text-muted-foreground">·</span>
                        <span className="text-xs text-muted-foreground">{file.size}</span>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground group-hover:text-foreground" />
                  </button>
                ))}
              </div>
            </div>
          ))
      )}

      <ReceiptSnap open={receiptOpen} onClose={() => setReceiptOpen(false)} />

      {/* Preview modal (mock) */}
      {previewFile && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm">
          <div className="absolute inset-x-0 bottom-0 top-12 mx-auto w-full max-w-[560px] overflow-hidden rounded-t-3xl bg-card shadow-2xl">
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-foreground">{previewFile.name}</p>
                <p className="text-xs text-muted-foreground">{previewFile.date} · {previewFile.size}</p>
              </div>
              <button
                onClick={() => setPreviewFile(null)}
                className="ml-3 flex h-9 w-9 items-center justify-center rounded-full bg-muted text-muted-foreground hover:bg-secondary"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex h-[calc(100%-140px)] items-center justify-center bg-muted/40 p-8">
              <div className="text-center">
                <FileText className="mx-auto h-16 w-16 text-muted-foreground/50" />
                <p className="mt-4 text-sm font-medium text-foreground">PDF preview</p>
                <p className="mt-1 text-xs text-muted-foreground">This is a placeholder - real PDF viewer coming next</p>
              </div>
            </div>
            <div className="border-t border-border bg-card p-4">
              <div className="grid grid-cols-2 gap-3">
                <button className="flex items-center justify-center gap-2 rounded-xl bg-muted py-3 text-sm font-medium text-foreground hover:bg-secondary">
                  <Eye className="h-4 w-4" /> Open
                </button>
                <button className="flex items-center justify-center gap-2 rounded-xl bg-primary py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90">
                  <Download className="h-4 w-4" /> Download
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
